"use client"

import { collectSuperProperties } from "./shared/superProperties"
import type { BaseAnalyticsEvent, QueuedEvent } from "./shared/types"

let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let lifecycleInitialized = false

const MAX_BATCH = 20
const IDLE_DELAY_MS = 2000
// 32 KiB — defensive guard against the 64 KiB per-origin keepalive cap
const BODY_SIZE_LIMIT = 32_768

// Extend RequestInit so TypeScript accepts the Fetch spec's priority field
type AnalyticsFetchInit = RequestInit & { priority?: "high" | "low" | "auto" }

const cancelTimer = () => {
  if (flushTimer === null) return
  clearTimeout(flushTimer)
  flushTimer = null
}

const scheduleFlush = (delayMs: number) => {
  cancelTimer()
  flushTimer = setTimeout(() => {
    flushTimer = null
    // requestIdleCallback is not implemented in jsdom — the else branch is the
    // test path. Both branches call flush(); the idle callback only defers on
    // real browsers.
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(flush, { timeout: IDLE_DELAY_MS })
    } else {
      flush()
    }
  }, delayMs)
}

const sendBatch = (events: QueuedEvent[]) => {
  const init: AnalyticsFetchInit = {
    method: "POST",
    keepalive: true,
    priority: "low",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events }),
  }
  fetch("/api/analytics/events", init as RequestInit).catch(() => {})
}

export const flush = (): void => {
  if (queue.length === 0) return

  const toFlush = queue
  queue = []

  const body = JSON.stringify({ events: toFlush })
  if (body.length > BODY_SIZE_LIMIT && toFlush.length > 1) {
    // Batch too large — split in two. Re-queue the second half and send the
    // first immediately. The re-queue triggers a new scheduleFlush(0).
    const half = Math.ceil(toFlush.length / 2)
    queue = toFlush.slice(half)
    sendBatch(toFlush.slice(0, half))
    scheduleFlush(0)
    return
  }

  sendBatch(toFlush)
}

export const track = <E extends BaseAnalyticsEvent>(event: E): void => {
  queue.push({
    id: crypto.randomUUID(),
    eventName: event.name,
    superProperties: collectSuperProperties(),
    clientTimestamp: new Date().toISOString(),
    ...(event.properties != null ? { eventProperties: event.properties } : undefined),
    ...(event.userPropertyIncrements != null
      ? { userPropertyIncrements: event.userPropertyIncrements }
      : undefined),
    ...(event.userProperties != null
      ? { userProperties: event.userProperties }
      : undefined),
  })

  if (queue.length >= MAX_BATCH) {
    scheduleFlush(0)
  } else {
    scheduleFlush(IDLE_DELAY_MS)
  }
}

export const initAnalyticsLifecycle = (): void => {
  if (lifecycleInitialized) return
  lifecycleInitialized = true

  let flushed = false
  const flushOnce = () => {
    if (flushed) return
    flushed = true
    cancelTimer()
    flush()
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnce()
  })
  window.addEventListener("pagehide", flushOnce)
}

// Test-only — not part of the public API. Resets all module-level state so
// tests start from a clean queue without re-importing the module.
export const __resetForTesting = (): void => {
  queue = []
  if (flushTimer !== null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  lifecycleInitialized = false
}
